/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/explicit-member-accessibility */
/* eslint-disable no-magic-numbers */
import { Vector2 } from '@technology/web3d-plugin-threejs';
import { Api } from '@technology/web3d/dist/esnext/Api.js';
import { Web3DPlugin } from '@technology/web3d/dist/esnext/Web3DPlugin.js';
import proj4 from 'proj4';
import {
  FeaturesServiceSceneProjection,
  FeaturesServiceSettings,
} from './config/features-service-settings.js';
import { FeaturesServiceModel } from './features-service-model.js';
import {
  TglElevationTransformFunction,
  TglTransformFunction,
  getTglElevationTransformFunction,
  getTglTransformFunction,
  initTgl,
} from './tgl/tgl.js';
import { FeaturesServiceOptions } from './types/features-service-options.js';

export type Coordinates = { x: number; y: number };

// Helper functions to convert between lat/lon and tile coordinates. These are based on the Web Mercator projection and TMS tiling scheme, and may need to be adjusted if using a different projection or tiling scheme.

function latitudeToTMSY(
  lat: number,
  zoom: number
): { tmsY: number; tmsYRaw: number } {
  const tmsYRaw = ((90 + lat) / 180) * (1 << zoom);
  const tmsY = Math.round(((90 + lat) / 180) * (1 << zoom) - 0.5);
  return { tmsY, tmsYRaw };
}

function longitudeToTMSX(
  lon: number,
  zoom: number
): { tmsX: number; tmsXRaw: number } {
  const tmsXRaw = (lon / 180) * (1 << zoom) + (1 << zoom) - 0.5;
  const tmsX = Math.round((lon / 180) * (1 << zoom) + (1 << zoom) - 0.5);

  return { tmsX, tmsXRaw };
}

export class FeaturesServicePlugin extends Web3DPlugin {
  constructor(private sceneProjection?: FeaturesServiceSceneProjection) {
    super();
    // if (!(sceneProjection.proj4def || sceneProjection.tglDef))
    //   throw new Error('Projection definition is missing');
  }

  public get name(): string {
    return 'featuresservice';
  }

  public set api(api: Api) {
    this._api = api;
  }

  private _api!: Api;
  private tglTransform?: TglTransformFunction;
  private tglElevationTransform?: TglElevationTransformFunction;

  public getTileIndexFromLatLon(
    lat: number,
    lon: number,
    zoom: number
  ): Vector2 {
    const { tmsX } = longitudeToTMSX(lon, zoom);
    const { tmsY } = latitudeToTMSY(lat, zoom);

    return new Vector2(tmsX, tmsY);
  }

  public getTileCoordinateFromCartographic(
    coordinate: Coordinates,
    zoomLevel: number,
    flipYCoordinate: boolean = true
  ): Coordinates {
    const latRad = (coordinate.y * Math.PI) / 180;
    const n = Math.pow(2, zoomLevel);
    const xCoord = Math.floor(((coordinate.x + 180.0) / 360.0) * n);
    const yCoord = Math.floor(
      ((1.0 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2.0) * n
    );
    return new Vector2(xCoord, flipYCoordinate ? n - yCoord - 1 : yCoord);
  }

  /* @deprecated - Targets WGS84 tiles */
  public getTileCoordsFromLatLon(
    latDeg: number,
    lonDeg: number,
    zoom: number
  ): Vector2 {
    const n = Math.pow(2, zoom);
    const xCoord = Math.floor(((lonDeg + 180.0) / 180.0) * n); // divide by 180 instead of 360 because of bounds only from 0 to 180
    const yCoord = Math.floor(((90.0 + latDeg) / 180.0) * n);
    return new Vector2(xCoord, yCoord);
  }

  getLonLatFromModelCoordinate(position: Vector2) {
    if (!this.sceneProjection) {
      throw new Error(
        'Scene projection is not set. Please set it before loading the model.'
      );
    }
    let pointLonLat: any;
    pointLonLat = proj4.transform(
      proj4.Proj(this.sceneProjection.proj4def!),
      proj4.Proj('WGS84'),
      position,
      false
    )!;

    return { x: pointLonLat.x, y: pointLonLat.y };
  }

  public getTileCoordinate(position: Vector2, zoomLevel: number): Vector2 {
    const pointLonLat = this.getLonLatFromModelCoordinate(position);
    return this.getTileCoordsFromLatLon(
      pointLonLat.y,
      pointLonLat.x,
      zoomLevel
    );
  }

  setSceneProjection(sceneProjection: FeaturesServiceSceneProjection) {
    if (this.sceneProjection) {
      console.warn('Scene projection is already set, returning');
      return;
    }
    this.sceneProjection = sceneProjection;
  }

  load(options: FeaturesServiceOptions): FeaturesServiceModel {
    if (!this.sceneProjection) {
      throw new Error(
        'Scene projection is not set. Please set it before loading the model.'
      );
    }

    const model = new FeaturesServiceModel(
      new FeaturesServiceSettings(
        options,
        this.sceneProjection,
        this.tglTransform,
        this.tglElevationTransform
      ),
      this._api
    );
    this._api.models?.add(model);
    return model;
  }

  /** Initializes TGL horizontal (and optional vertical) transforms from the scene CSIB(s). */
  public async initTglTransforms(
    geodeticGrids?: Map<string, ArrayBuffer>
  ): Promise<void> {
    if (this.sceneProjection?.tglDef) {
      await initTgl();
      this.tglTransform = getTglTransformFunction(
        this.sceneProjection.tglDef,
        geodeticGrids
      );
      this.tglElevationTransform = this.sceneProjection.verticalTglDef
        ? getTglElevationTransformFunction(
            this.sceneProjection.verticalTglDef,
            geodeticGrids
          )
        : undefined;
      this.setSceneProjection(this.sceneProjection);
    } else {
      throw new Error('No tglDef found in sceneProjection');
    }
  }
}
